import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";
import { withStyles } from "@mui/styles";
import { GetStateStorage } from "common/storage";
import { safeDateFormat } from "common/helpers/dateUtils";
import { GetLegalContractForAgent } from "services/agent";
import styles from "./styles";

const decodeHtmlEntities = (value = "") => {
  if (!value || typeof document === "undefined") {
    return value || "";
  }

  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
};

const DynamicContract = ({
  classes,
  agentFirstName = "",
  agentLastName = "",
  agentRegistrationDate = "",
  listgenUserID = "",
  hideSignatureSection = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [legalHTMLTemplate, setLegalHTMLTemplate] = useState("");
  const [legalCSS, setLegalCSS] = useState("");
  const [, setDocSignedOnDate] = useState("");

  const effectiveListgenUserID = useMemo(() => {
    if (listgenUserID) {
      return String(listgenUserID);
    }
    const storageData = GetStateStorage() || {};
    return storageData?.userData?.listgenUserID || "";
  }, [listgenUserID]);

  useEffect(() => {
    const fetchDynamicContract = async () => {
      if (!effectiveListgenUserID) {
        setError("Unable to load legal contract.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        const response = await GetLegalContractForAgent(effectiveListgenUserID);
        const nextHTML = decodeHtmlEntities(response?.docHTML || "");
        const nextCSS = decodeHtmlEntities(response?.docCSS || "");
        const nextSignedOnDate = response?.docSignedOnDate || "";

        setLegalHTMLTemplate(nextHTML);
        setLegalCSS(nextCSS);
        setDocSignedOnDate(nextSignedOnDate);

        if (!nextHTML) {
          setError("Legal contract content is unavailable.");
        }
      } catch (err) {
        setError(err || "Unable to load legal contract.");
      } finally {
        setLoading(false);
      }
    };

    fetchDynamicContract();
  }, [effectiveListgenUserID]);

  return (
    <div className={classes.root}>
      {loading && (
        <Typography variant="body1" className={classes.statusText}>
          Loading legal contract...
        </Typography>
      )}

      {!loading && error && (
        <Typography variant="body1" color="error" className={classes.statusText}>
          {error}
        </Typography>
      )}

      {!loading && legalCSS && (
        <style
          type="text/css"
          dangerouslySetInnerHTML={{ __html: legalCSS }}
        />
      )}

      {!loading && legalHTMLTemplate && (
        <div
          className={classes.contractContainer}
          dangerouslySetInnerHTML={{ __html: legalHTMLTemplate }}
        />
      )}

      {!hideSignatureSection && agentRegistrationDate && (
        <div className={classes.signatureContainer}>
          <div className={classes.signatureBox}>
            <div className={classes.signatureRow}>
              <Typography
                variant="body1"
                component="span"
                className={classes.signatureLabel}
              >
                Signed and agreed by:
              </Typography>
              <span className={classes.signatureValueLine}>
                <Typography variant="body1" component="span">
                  {agentFirstName} {agentLastName}
                </Typography>
              </span>
            </div>
            <div className={classes.signatureRow}>
              <Typography
                variant="body1"
                component="span"
                className={classes.signatureLabel}
              >
                Signed on:
              </Typography>
              <span className={classes.signatureValueLine}>
                <Typography variant="body1" component="span">
                  {/* TODO: switch back to docSignedOnDate once backend migration is complete. */}
                  {safeDateFormat(
                    agentRegistrationDate,
                    "MMMM DD, YYYY hh:mm A",
                    ""
                  )}
                </Typography>
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

DynamicContract.propTypes = {
  classes: PropTypes.object.isRequired,
  agentFirstName: PropTypes.string,
  agentLastName: PropTypes.string,
  agentRegistrationDate: PropTypes.string,
  listgenUserID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  hideSignatureSection: PropTypes.bool,
};

export default withStyles(styles)(DynamicContract);
